"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, Cpu, Database, Network, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const aiModelsData = [{
  id: "avixa-cts-d",
  title: "AVIXA\nCTS, CTS-I, CTS-D\xA0 and ANP Certified",
  category: "INTEGRATION + DESIGN ENGINEERING",
  description: "Specialized certification for audiovisual system installation, design, and networking focusing on precision engineering and integrated solution planning for enterprise environments.",
  icon: Activity,
  specs: [{
    label: "Standard",
    value: "ANSI/INFOCOMM"
  }, {
    label: "Category",
    value: "Design"
  }, {
    label: "Status",
    value: "Verified"
  }, {
    label: "Renewal",
    value: "Active"
  }],
  actionText: "View Solutions",
  actionLink: "/SolutionsPage"
}, {
  id: "crestron-master",
  title: "Crestron\xA0",
  category: "System Automation",
  description: "Advanced control system programming certification for complex enterprise-grade automation and management of workplace technology.",
  icon: Network,
  specs: [{
    label: "Tier",
    value: "Silver Master"
  }, {
    label: "System",
    value: "Control"
  }, {
    label: "Platform",
    value: "NVX/VC-4"
  }, {
    label: "Status",
    value: "Verified"
  }],
  actionText: "Learn More",
  actionLink: "/SolutionsPage"
}, {
  id: "dante-level-3",
  title: "Dante",
  category: "Network Audio",
  description: "Audio-over-IP networking, enabling complex multi-subnet system architecture and synchronization.",
  icon: Database,
  specs: [{
    label: "Protocol",
    value: "Dante"
  }, {
    label: "Level",
    value: "3 (Expert)"
  }, {
    label: "Topology",
    value: "Multi-Subnet"
  }, {
    label: "Status",
    value: "Verified"
  }],
  actionText: "View Solutions",
  actionLink: "/SolutionsPage"
}];
export default function AIModels() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="152200" id="certifications" className="dark bg-background py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          
          <h2 className="text-3xl font-sans font-bold tracking-tighter mb-6 uppercase !text-[hsl(0,92%,40%)] md:text-4xl lg:text-5xl">
            Certified Technical Expertise
          </h2>
          <p className="text-muted-foreground font-mono text-base md:text-lg leading-relaxed">
            WTS maintains the highest level of technical proficiency through rigorous industry-standard training and manufacturer-specific engineering certifications.
          </p>
        </div>

        {}
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {aiModelsData.map((model, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          const Icon = model.icon;
          return <div key={model.id} data-index={i} ref={cardRef} className={`col-span-12 md:col-span-6 lg:col-span-4 transition-all duration-1000 ease-out ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="h-full flex flex-col bg-[#171717] border-t border-t-transparent hover:border-t-primary rounded-[8px] overflow-hidden group transition-all duration-300 shadow-none">
                  <CardHeader className="pb-4">
                    <div className="flex justify-between items-start mb-4">
                      <div className="p-2.5 bg-primary/10 rounded-none text-primary">
                        <Icon className="w-6 h-6" />
                      </div>
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0 font-mono tracking-wider uppercase text-[10px]">
                        {model.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl text-foreground font-sans font-bold tracking-tight mb-2">
                      {model.title}
                    </CardTitle>
                    <CardDescription className="font-sans text-muted-foreground text-sm leading-relaxed min-h-[60px]">
                      {model.description}
                    </CardDescription>
                  </CardHeader>
                  
                  
                  
                  <CardFooter className="pt-0 mt-auto">
                    <Button asChild className="w-full rounded-[4px] bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase transition-all" size="lg">
                      
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

      </div>
    </section>;
}